import { useEffect, useRef, useState } from 'react'
import { LuWallet, LuArrowLeftRight, LuList, LuLink, LuArrowLeft } from 'react-icons/lu'
import {
  getChileBalance,
  getChileMovements,
  getChileTransferConfig,
  getChileReceiveConfig,
  createChileTransfer,
  waitChileTransfer,
  createChileCollection
} from '../services/mwZento'
import './PortalTools.css'

const ACTIONS = [
  { id: 'cobro', label: 'Generar cobro', icon: LuLink },
  { id: 'enviar', label: 'Enviar dinero', icon: LuArrowLeftRight },
  { id: 'movimientos', label: 'Movimientos', icon: LuList },
  { id: 'saldo', label: 'Consultar saldo', icon: LuWallet }
]

function ChilePage() {
  const [section, setSection] = useState(null)

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><LuWallet /> CLP</h1>
      </div>

      {section == null ? (
        <div className="clp-actions">
          {ACTIONS.map((action) => {
            const Icon = action.icon
            return (
              <button key={action.id} type="button" className="clp-action" onClick={() => setSection(action.id)}>
                <Icon />
                <span>{action.label}</span>
              </button>
            )
          })}
        </div>
      ) : (
        <Section section={section} onBack={() => setSection(null)} />
      )}
    </div>
  )
}

function Section({ section, onBack }) {
  return (
    <div>
      <button type="button" className="market-back clp-back" onClick={onBack}>
        <LuArrowLeft /> Volver
      </button>
      {section === 'saldo' && <BalanceSection />}
      {section === 'movimientos' && <MovementsSection />}
      {section === 'enviar' && <SendSection />}
      {section === 'cobro' && <ChargeSection onDone={onBack} />}
    </div>
  )
}

function money(value) {
  return Number(value || 0).toLocaleString('es-CL')
}

const STATUS_LABEL = {
  processing: 'En proceso',
  success: 'Acreditado',
  failed: 'Rechazado',
  expired: 'Vencido',
  refunded: 'Devuelto'
}

function BalanceSection() {
  const [balance, setBalance] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let stopped = false
    getChileBalance()
      .then((result) => { if (!stopped) setBalance(result) })
      .catch((err) => { if (!stopped) setError(err.message) })
      .finally(() => { if (!stopped) setLoading(false) })
    return () => { stopped = true }
  }, [])

  return (
    <div className="clp-balance">
      <p className="clp-kicker">Saldo CLP</p>
      {loading && (
        <div className="clp-loading">
          <span className="clp-spinner" />
          <span>Cargando</span>
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}
      {!loading && balance && (
        <div className="clp-balance-lines">
          <p className="clp-balance-total"><span>Saldo total</span><strong>$ {money(balance.balance)}</strong></p>
          <p className="clp-balance-available"><span>Disponible para transferir</span><strong>$ {money(balance.availableForPayout)}</strong></p>
        </div>
      )}
    </div>
  )
}

function movementTone(status) {
  if (status === 'success') return 'ok'
  if (status === 'failed') return 'bad'
  return 'wait'
}

function MovementsSection() {
  const [items, setItems] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState({ amount: '', paymentMethod: '', id: '', merchantReference: '' })
  const requestId = useRef(0)
  const pageSize = 50
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  const onFilter = (event) => {
    const { name, value } = event.target
    setPage(1)
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  useEffect(() => {
    const current = requestId.current + 1
    requestId.current = current
    setLoading(true)
    getChileMovements({
      page,
      pageSize,
      amount: filters.amount.trim(),
      paymentMethod: filters.paymentMethod.trim(),
      id: filters.id.trim(),
      merchantReference: filters.merchantReference.trim()
    })
      .then((result) => {
        if (requestId.current !== current) return
        setItems(result.items || [])
        setTotal(result.pagination?.total || 0)
        setError('')
      })
      .catch((err) => {
        if (requestId.current !== current) return
        setError(err.message)
      })
      .finally(() => {
        if (requestId.current === current) setLoading(false)
      })
  }, [filters, page])

  return (
    <div className="clp-movements">
      <h3>Movimientos</h3>
      <label>Monto</label>
      <input className="form-control" name="amount" value={filters.amount} onChange={onFilter} placeholder="Buscar por monto" />
      <div className="clp-filters">
        <label>
          Método de pago
          <input className="form-control" name="paymentMethod" value={filters.paymentMethod} onChange={onFilter} />
        </label>
        <label>
          Id
          <input className="form-control" name="id" value={filters.id} onChange={onFilter} />
        </label>
        <label>
          merchantReference
          <input className="form-control" name="merchantReference" value={filters.merchantReference} onChange={onFilter} />
        </label>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && (
        <div className="clp-loading">
          <span className="clp-spinner" />
          <span>Cargando</span>
        </div>
      )}
      {!loading && items.length === 0 && <p className="tool-desc">No hay movimientos.</p>}
      {!loading && items.length > 0 && (
        <ul className="clp-movement-list">
          {items.map((item) => (
            <li key={`${item.id}-${item.merchantReference}`} className={`clp-movement clp-movement-${movementTone(item.status)}`}>
              <div className="clp-movement-top">
                <strong>{item.direction === 'in' ? '+' : '−'} $ {money(item.amount)}</strong>
                <span>{STATUS_LABEL[item.status] || item.status}</span>
              </div>
              <p className="clp-movement-field"><span>Id</span>{item.id}</p>
              <p className="clp-movement-field"><span>Referencia</span>{item.merchantReference}</p>
            </li>
          ))}
        </ul>
      )}
      {total > pageSize && (
        <div className="clp-pages">
          <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Anterior</button>
          <span>Página {page} de {pageCount}</span>
          <button type="button" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>Siguiente</button>
        </div>
      )}
    </div>
  )
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[char]))
}

function printChileReceipt(receipt) {
  const printWindow = window.open('', '_blank')
  if (!printWindow) return
  const when = new Date().toLocaleString('es-CL')
  printWindow.document.write(`<!DOCTYPE html>
    <html lang="es"><head><meta charset="utf-8" />
    <title>Comprobante ${escapeHtml(receipt.id)}</title>
    <style>
      body { margin: 0; background: #F4F5F7; font-family: "Plus Jakarta Sans", system-ui, sans-serif; color: #111; }
      .sheet { max-width: 640px; margin: 32px auto; background: #fff; padding: 48px 48px 36px; position: relative; }
      .sheet::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: #1D4ED8; }
      h1 { font-size: 22px; margin: 0 0 8px; }
      .amount { font-size: 32px; font-weight: 800; letter-spacing: -0.04em; margin: 28px 0; }
      p { margin: 0 0 14px; }
      span { display: block; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #6B7280; }
      strong { font-size: 16px; }
    </style></head><body>
    <div class="sheet">
      <h1>Comprobante de transferencia</h1>
      <p><span>Estado</span><strong>Enviada</strong></p>
      <p class="amount">− $ ${escapeHtml(receipt.amount)}</p>
      <p><span>Destinatario</span><strong>${escapeHtml(receipt.name)}</strong></p>
      <p><span>RUT</span><strong>${escapeHtml(receipt.rut)}</strong></p>
      <p><span>Banco</span><strong>${escapeHtml(receipt.bank)}</strong></p>
      <p><span>Cuenta</span><strong>${escapeHtml(receipt.account)}</strong></p>
      <p><span>Id</span><strong>${escapeHtml(receipt.id)}</strong></p>
      <p><span>Referencia</span><strong>${escapeHtml(receipt.reference)}</strong></p>
      <p><span>Fecha</span><strong>${escapeHtml(when)}</strong></p>
    </div>
    <script>window.onload = function () { window.print() }<\/script>
    </body></html>`)
  printWindow.document.close()
}

function printChargeReceipt(receipt) {
  const printWindow = window.open('', '_blank')
  if (!printWindow) return
  printWindow.document.write(`<!DOCTYPE html>
    <html lang="es"><head><meta charset="utf-8" />
    <title>Comprobante ${escapeHtml(receipt.id)}</title>
    <style>
      body { margin: 0; background: #F4F5F7; font-family: "Plus Jakarta Sans", system-ui, sans-serif; color: #111; }
      .sheet { max-width: 640px; margin: 32px auto; background: #fff; padding: 48px 48px 36px; position: relative; }
      .sheet::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: #16A34A; }
      h1 { font-size: 22px; margin: 0 0 8px; }
      .amount { font-size: 32px; font-weight: 800; letter-spacing: -0.04em; margin: 28px 0; color: #1D4ED8; }
      p { margin: 0 0 14px; }
      span { display: block; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #6B7280; }
      strong { font-size: 16px; }
    </style></head><body>
    <div class="sheet">
      <h1>Comprobante de cobro</h1>
      <p><span>Estado</span><strong>Aprobada</strong></p>
      <p class="amount">+ $ ${escapeHtml(receipt.amount)}</p>
      <p><span>Hora</span><strong>${escapeHtml(receipt.paidAt)}</strong></p>
      <p><span>Desde</span><strong>${escapeHtml(receipt.from)}</strong></p>
      <p><span>Hasta</span><strong>Caja CLP</strong></p>
      <p><span>Banco</span><strong>${escapeHtml(receipt.bank)}</strong></p>
      <p><span>RUT</span><strong>${escapeHtml(receipt.rut)}</strong></p>
      <p><span>Email</span><strong>${escapeHtml(receipt.email)}</strong></p>
      <p><span>Id</span><strong>${escapeHtml(receipt.id)}</strong></p>
      <p><span>Referencia</span><strong>${escapeHtml(receipt.reference)}</strong></p>
    </div>
    <script>window.onload = function () { window.print() }<\/script>
    </body></html>`)
  printWindow.document.close()
}

function SendSection() {
  const [banks, setBanks] = useState([])
  const [available, setAvailable] = useState(null)
  const [error, setError] = useState('')
  const [outcome, setOutcome] = useState('')
  const [receipt, setReceipt] = useState(null)
  const [sending, setSending] = useState(false)
  const [transferId, setTransferId] = useState('')
  const [ready, setReady] = useState(false)
  const [form, setForm] = useState({ amount: '', name: '', rut: '', email: '', bankCode: '', accountNumber: '' })

  useEffect(() => {
    let stopped = false
    Promise.all([
      getChileTransferConfig().then((config) => { if (!stopped) setBanks(config.banks || []) }),
      getChileBalance().then((balance) => { if (!stopped) setAvailable(Number(balance.availableForPayout || 0)) })
    ]).catch((err) => { if (!stopped) setError(err.message) })
      .finally(() => { if (!stopped) setReady(true) })
    return () => { stopped = true }
  }, [])

  useEffect(() => {
    if (!transferId) return undefined
    const controller = new AbortController()
    let stopped = false
    const watch = async () => {
      while (!stopped) {
        try {
          const result = await waitChileTransfer(transferId, controller.signal)
          if (stopped) return
          if (result.status === 'success') {
            setOutcome('sent')
            setReceipt((current) => ({ ...current, id: result.id || current?.id, reference: result.merchantTransactionReference || current?.reference }))
            setTransferId('')
            return
          }
          if (result.status === 'failed' || result.status === 'expired' || result.status === 'refunded') {
            setOutcome('rejected')
            setReceipt(null)
            setTransferId('')
            return
          }
        } catch (err) {
          if (stopped || err.name === 'AbortError') return
          await new Promise((resolve) => setTimeout(resolve, 3000))
        }
      }
    }
    watch()
    return () => {
      stopped = true
      controller.abort()
    }
  }, [transferId])

  const onChange = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    const amount = Number(form.amount)
    if (available != null && amount > available) {
      setError(`No alcanza el saldo. Disponible para transferir $ ${money(available)}.`)
      return
    }
    setSending(true)
    setError('')
    setOutcome('sending')
    setReceipt(null)
    try {
      const bank = banks.find((item) => item.code === form.bankCode)
      const result = await createChileTransfer({
        amount: Number(form.amount),
        recipient: {
          name: form.name,
          rut: form.rut,
          email: form.email,
          bankCode: form.bankCode,
          accountNumber: form.accountNumber
        }
      }, crypto.randomUUID())
      setReceipt({
        id: result.id,
        reference: result.merchantTransactionReference,
        amount: money(form.amount),
        name: form.name,
        rut: form.rut,
        email: form.email,
        bank: bank?.name || form.bankCode,
        account: form.accountNumber
      })
      if (result.status === 'success') setOutcome('sent')
      else if (result.status === 'failed') {
        setOutcome('rejected')
        setReceipt(null)
      } else if (result.id) setTransferId(result.id)
    } catch (err) {
      setOutcome('')
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  if (!ready) {
    return (
      <div className="tool-card clp-send-loading">
        <span className="clp-spinner" />
        <p>Cargando</p>
      </div>
    )
  }

  return (
    <form className="tool-card clp-send" onSubmit={onSubmit}>
      <div className="clp-send-form">
      <h3>Enviar dinero</h3>
      {available != null && <p className="clp-send-available">Disponible para transferir <span>$ {money(available)}</span></p>}
      {error && <div className="alert alert-error">{error}</div>}
      <label>Monto CLP</label>
      <input className="form-control" name="amount" value={form.amount} onChange={onChange} required />
      <label>Nombre completo</label>
      <input className="form-control" name="name" value={form.name} onChange={onChange} required />
      <label>RUT</label>
      <input className="form-control" name="rut" value={form.rut} onChange={onChange} required />
      <label>Email</label>
      <input className="form-control" type="email" name="email" value={form.email} onChange={onChange} required />
      <label>Banco</label>
      <select className="form-control" name="bankCode" value={form.bankCode} onChange={onChange} required>
        <option value="">Seleccionar</option>
        {banks.map((bank) => <option key={bank.code} value={bank.code}>{bank.name}</option>)}
      </select>
      <label>Número de cuenta</label>
      <input className="form-control" name="accountNumber" value={form.accountNumber} onChange={onChange} required />
      <button className="btn-primary clp-submit" disabled={sending || outcome === 'sending'}>Confirmar transferencia</button>
      </div>
      <aside className="clp-send-art" aria-hidden="true">
        <span>FLASH</span>
        <p>Transferencias en pesos chilenos</p>
      </aside>
      {outcome === 'sending' && (
        <div className="clp-modal-backdrop">
          <div className="clp-modal" role="dialog" aria-modal="true">
            <span className="clp-spinner" />
            <h3>Enviando transferencia...</h3>
            <button type="button" className="clp-modal-close" onClick={() => { setOutcome(''); setTransferId('') }}>Cerrar</button>
          </div>
        </div>
      )}
      {outcome === 'sent' && receipt && (
        <div className="clp-modal-backdrop">
          <div className="clp-modal clp-modal-sent" role="dialog" aria-modal="true">
            <p className="clp-modal-kicker">Enviada correctamente</p>
            <h3>$ {receipt.amount}</h3>
            <dl className="clp-modal-facts">
              <div><dt>Desde</dt><dd>Caja CLP</dd></div>
              <div><dt>Hasta</dt><dd>{receipt.name}<br />{receipt.account}</dd></div>
              <div><dt>Banco</dt><dd>{receipt.bank}</dd></div>
              <div><dt>RUT</dt><dd>{receipt.rut}</dd></div>
              <div><dt>Email</dt><dd>{receipt.email}</dd></div>
              <div><dt>Id</dt><dd>{receipt.id}</dd></div>
              <div><dt>Referencia</dt><dd>{receipt.reference}</dd></div>
            </dl>
            <button type="button" className="btn-primary" onClick={() => printChileReceipt(receipt)}>Generar comprobante</button>
            <button type="button" className="clp-modal-close" onClick={() => setOutcome('')}>Cerrar</button>
          </div>
        </div>
      )}
      {outcome === 'rejected' && (
        <div className="clp-modal-backdrop">
          <div className="clp-modal" role="dialog" aria-modal="true">
            <h3>Transferencia rechazada</h3>
            <button type="button" className="clp-modal-close" onClick={() => setOutcome('')}>Cerrar</button>
          </div>
        </div>
      )}
    </form>
  )
}

function ChargeSection({ onDone }) {
  const [banks, setBanks] = useState([])
  const [error, setError] = useState('')
  const [link, setLink] = useState('')
  const [charge, setCharge] = useState(null)
  const [sending, setSending] = useState(false)
  const [collectionId, setCollectionId] = useState('')
  const [phase, setPhase] = useState('form')
  const paymentWindow = useRef(null)
  const [form, setForm] = useState({ amount: '', rut: '', email: '', bankCode: '' })
  const selectedBank = banks.find((bank) => `${bank.paymentMethod || 'bank_transfer'}|${bank.code}` === form.bankCode)
  const transferBank = selectedBank?.paymentMethod === 'bank_transfer'
  const asksRut = selectedBank
    ? (Array.isArray(selectedBank.fields) ? selectedBank.fields.includes('rut') : transferBank)
    : false
  const asksEmail = selectedBank
    ? (Array.isArray(selectedBank.fields) ? selectedBank.fields.includes('email') : transferBank)
    : false

  useEffect(() => {
    let stopped = false
    getChileReceiveConfig()
      .then((config) => { if (!stopped) setBanks(config.banks || []) })
      .catch((err) => { if (!stopped) setError(err.message) })
    return () => { stopped = true }
  }, [])

  const onChange = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const closePaymentWindow = () => {
    const popup = paymentWindow.current
    if (!popup) return
    const shut = () => {
      try { popup.close() } catch { /* El navegador no dejó cerrar la ventana del cobro. */ }
    }
    shut()
    window.setTimeout(shut, 200)
    window.setTimeout(shut, 800)
  }

  useEffect(() => {
    if (phase !== 'waiting' || !collectionId) return undefined
    const controller = new AbortController()
    let stopped = false
    const watch = async () => {
      while (!stopped) {
        try {
          const result = await waitChileTransfer(collectionId, controller.signal)
          if (stopped) return
          if (result.status === 'success') {
            closePaymentWindow()
            setCharge((current) => ({
              ...current,
              id: result.id || current?.id,
              reference: result.merchantTransactionReference || current?.reference,
              paidAt: new Date().toLocaleString('es-CL')
            }))
            setPhase('approved')
            return
          }
          if (result.status === 'failed' || result.status === 'expired' || result.status === 'refunded') {
            closePaymentWindow()
            setError(result.status === 'expired' ? 'El cobro venció' : 'El cobro no se completó')
            setPhase('form')
            return
          }
        } catch (err) {
          if (stopped || err.name === 'AbortError') return
          await new Promise((resolve) => setTimeout(resolve, 3000))
        }
      }
    }
    watch()
    return () => {
      stopped = true
      controller.abort()
    }
  }, [phase, collectionId])

  const onSubmit = async (event) => {
    event.preventDefault()
    setSending(true)
    setError('')
    setLink('')
    try {
      const [paymentMethod, bankCode] = String(form.bankCode).includes('|')
        ? form.bankCode.split('|')
        : ['bank_transfer', form.bankCode]
      const bank = banks.find((item) => `${item.paymentMethod || 'bank_transfer'}|${item.code}` === form.bankCode)
      const payer = { bankCode, paymentMethod }
      if (asksRut) payer.rut = form.rut
      if (asksEmail) payer.email = form.email
      const result = await createChileCollection({
        amount: Number(form.amount),
        payer
      })
      const from = [form.rut, form.email].filter(Boolean).join(' · ') || 'Pagador'
      setCharge({
        id: result.id,
        reference: result.merchantTransactionReference,
        amount: money(form.amount),
        bank: bank?.name || bankCode,
        rut: form.rut || '—',
        email: form.email || '—',
        from,
        paidAt: ''
      })
      const url = result.paymentInstructions?.url || ''
      setLink(url)
      setCollectionId(result.id || '')
      setPhase('waiting')
      if (url) {
        paymentWindow.current = window.open(url, 'flash-cobro', 'popup,width=480,height=820')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  return (
    <form className="tool-card clp-charge" onSubmit={onSubmit}>
      <h3>Generar cobro</h3>
      {error && <div className="alert alert-error">{error}</div>}
      {phase === 'form' && (
      <>
      <label>Monto CLP</label>
      <input className="form-control" name="amount" value={form.amount} onChange={onChange} required />
      {asksRut && (
        <>
          <label>RUT</label>
          <input className="form-control" name="rut" value={form.rut} onChange={onChange} required />
        </>
      )}
      {asksEmail && (
        <>
          <label>Email</label>
          <input className="form-control" type="email" name="email" value={form.email} onChange={onChange} required />
        </>
      )}
      <label>Banco</label>
      <select className="form-control" name="bankCode" value={form.bankCode} onChange={onChange} required>
        <option value="">Seleccionar</option>
        {banks.map((bank) => (
          <option key={`${bank.paymentMethod}-${bank.code}`} value={`${bank.paymentMethod || 'bank_transfer'}|${bank.code}`}>
            {bank.name}
          </option>
        ))}
      </select>
      <button className="btn-primary clp-submit" disabled={sending}>{sending ? 'Generando...' : 'Generar link'}</button>
      </>
      )}
      {phase === 'waiting' && (
        <div className="alert alert-success">
          <p>Por favor, continua en el link</p>
          {link && <a href={link} target="_blank" rel="noreferrer">{link}</a>}
        </div>
      )}
      {phase === 'approved' && charge && (
        <div className="clp-modal-backdrop">
          <div className="clp-modal clp-approved" role="dialog" aria-modal="true">
            <p className="clp-approved-kicker">Transacción aprobada</p>
            <h3 className="clp-approved-amount">$ {charge.amount}</h3>
            <p className="clp-approved-time">{charge.paidAt}</p>
            <div className="clp-approved-route">
              <div>
                <span>Desde</span>
                <strong>{charge.bank || 'Pagador'}</strong>
                {charge.rut !== '—' && <em>RUT {charge.rut}</em>}
                {charge.email !== '—' && <em>{charge.email}</em>}
              </div>
              <div>
                <span>Hasta</span>
                <strong>Caja CLP</strong>
              </div>
            </div>
            <dl className="clp-approved-meta">
              <div><dt>Id</dt><dd>{charge.id || '—'}</dd></div>
              <div><dt>Referencia</dt><dd>{charge.reference || '—'}</dd></div>
            </dl>
            <button type="button" className="btn-primary" onClick={() => printChargeReceipt(charge)}>Descargar comprobante</button>
            <button type="button" className="clp-modal-close" onClick={() => setPhase('form')}>Cerrar</button>
          </div>
        </div>
      )}
    </form>
  )
}

export default ChilePage
